import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { WaitingRoom } from './WaitingRoom';
import { FloatingVideoCall } from './FloatingVideoCall';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Loader2, 
  AlertCircle, 
  UserCheck
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { CustomerAvailabilityService } from '@/lib/services/customerAvailabilityService';
import { AvailabilityService } from '@/lib/services/availabilityService';
import { PublicCustomerInfo } from '@/types/availability';

interface PublicCustomerVideoCallProps {
  linkId: string;
}

type CallPhase = 'loading' | 'queue' | 'call' | 'error';

export function PublicCustomerVideoCall({ linkId }: PublicCustomerVideoCallProps) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [phase, setPhase] = useState<CallPhase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [customerInfo, setCustomerInfo] = useState<PublicCustomerInfo | null>(null);
  const [coachData, setCoachData] = useState<any>(null);
  const [coachId, setCoachId] = useState<string>('');
  const [roomId, setRoomId] = useState<string>('');

  useEffect(() => {
    if (!linkId) {
      setError('Invalid link');
      setPhase('error');
      return;
    }

    loadCustomerLinkData();
  }, [linkId]);

  const loadCustomerLinkData = async () => {
    try {
      setPhase('loading');
      
      // Get customer info from link (validates link automatically)
      const customerInfo = await CustomerAvailabilityService.getPublicCustomerInfo(linkId);
      if (!customerInfo) {
        setError(language === 'ar' ? 'الرابط غير صالح أو منتهي الصلاحية' : 'Invalid or expired link');
        setPhase('error');
        return;
      }

      setCustomerInfo(customerInfo);

      // Get the coach ID from the customer link
      const customerLink = await CustomerAvailabilityService.getCustomerLink(linkId);
      if (!customerLink) {
        setError(language === 'ar' ? 'الرابط غير صالح' : 'Invalid link');
        setPhase('error');
        return;
      }

      // Store coachId and load coach data
      setCoachId(customerLink.coachId);
      
      const coachDoc = await getDoc(doc(db, 'users', customerLink.coachId));
      if (!coachDoc.exists()) {
        setError(language === 'ar' ? 'المدرب غير موجود' : 'Coach not found');
        setPhase('error');
        return;
      }

      const coachInfo = coachDoc.data();
      setCoachData(coachInfo);

      // Automatically join the queue with the loaded data (not from state)
      await joinQueueWithData(customerInfo, customerLink.coachId);
    } catch (error) {
      console.error('Error loading customer link data:', error);
      setError(language === 'ar' ? 'فشل في تحميل البيانات' : 'Failed to load data');
      setPhase('error');
    }
  };

  const joinQueueWithData = async (customerData: PublicCustomerInfo, coachIdParam: string) => {
    try {
      await AvailabilityService.joinQueue(
        coachIdParam, 
        customerData.customerId, 
        customerData.customerName,
        customerData.customerPhone
      );
      
      
      setPhase('queue');
    } catch (error: any) {
      console.error('Error joining queue:', error);
      // If auto-join fails, show error phase
      setError(error.message || (language === 'ar' ? 'فشل في الانضمام للطابور' : 'Failed to join queue'));
      setPhase('error');
    }
  };

  const autoJoinQueue = async () => {
    if (!customerInfo || !coachId) {
      setError(language === 'ar' ? 'بيانات غير مكتملة' : 'Incomplete data');
      setPhase('error');
      return;
    }

    await joinQueueWithData(customerInfo, coachId);
  };

  const handleJoinQueue = async () => {
    // This function is kept for manual retry if auto-join fails
    await autoJoinQueue();
  };

  const handleCallStarted = (newRoomId: string) => {
    setRoomId(newRoomId);
    setPhase('call');
  };

  const handleCallEnded = () => {
    // After call ends, show completion message and allow navigation
    setError(language === 'ar' ? 'انتهت المكالمة بنجاح' : 'Call completed successfully');
    setPhase('error'); // Using error phase to show completion message with navigation
  };

  const handleLeaveQueue = () => {
    // When leaving queue, show completion message
    setError(language === 'ar' ? 'تم مغادرة غرفة الإنتظار' : 'Left the queue');
    setPhase('error');
  };

  const getCoachName = () => {
    if (!coachData) return language === 'ar' ? 'المدرب' : 'Coach';
    
    return language === 'ar'
      ? `${coachData.firstNameAr || coachData.firstName || 'مدرب'} ${coachData.lastNameAr || coachData.lastName || ''}`.trim()
      : `${coachData.firstNameEn || coachData.firstName || 'Coach'} ${coachData.lastNameEn || coachData.lastName || ''}`.trim();
  };


  // Loading phase
  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/20 p-4">
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-lg font-medium">
              {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {language === 'ar' ? 'جاري التحقق من الرابط والانضمام للطابور' : 'Verifying link and joining queue'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error phase
  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 to-secondary/20 p-4">
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Video className="h-12 w-12 text-green-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {language === 'ar' ? 'انتهت المكالمة بنجاح' : 'Call completed successfully'}
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              {error}
            </p>
            <Button onClick={() => navigate('/')}>
              {language === 'ar' ? 'العودة للرئيسية' : 'Go Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Queue phase
  if (phase === 'queue') {
    if (!customerInfo || !coachId) {
      // If we're in queue phase but missing data, show error
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 to-secondary/20 p-4">
          <Card className="w-full max-w-md mx-auto">
            <CardContent className="flex flex-col items-center justify-center py-8">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {language === 'ar' ? 'خطأ في البيانات' : 'Data Error'}
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                {language === 'ar' ? 'بيانات المستفيد أو المدرب غير متوفرة' : 'Customer or coach data not available'}
              </p>
              <Button onClick={() => navigate('/')}>
                {language === 'ar' ? 'العودة للرئيسية' : 'Go Home'}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <WaitingRoom
        coachId={coachId}
        coachName={getCoachName()}
        coachAvatar={coachData?.profilePicture}
        onCallStarted={handleCallStarted}
        onLeaveQueue={handleLeaveQueue}
        publicCustomerInfo={{
          customerId: customerInfo.customerId,
          customerName: customerInfo.customerName
        }}
      />
    );
  }

  // Call phase
  if (phase === 'call' && customerInfo) {
    return (
      <div className="fixed inset-0 bg-black z-50">
        <FloatingVideoCall 
          roomId={roomId}
          userRole="customer"
          userName={customerInfo.customerName}
          otherParticipantName={getCoachName()}
          onCallEnded={handleCallEnded}
          open={true}
          forceFullscreen={true}
        />
      </div>
    );
  }

  // This should never be reached as we go directly from loading to queue
  return null;
}
